"use client";
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { ROLE_LABELS, canViewReports, canManageMembers } from '@/lib/church-store';
import { useAuth } from '@/contexts/AuthContext';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { profile, signOut, loading } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  async function handleLogout() {
    await signOut();
    router?.push('/login');
  }

  const navItems = profile ? [
    { label: 'Members', href: '/members', show: true },
    { label: 'Visitor Intake', href: '/visitor-intake', show: canManageMembers(profile?.role) || ['greeter', 'usher']?.includes(profile?.role) },
    { label: 'Pipeline', href: '/pipeline', show: canManageMembers(profile?.role) || profile?.role === 'pastor' },
    { label: 'Reports', href: '/reports', show: canViewReports(profile?.role) },
    { label: 'Staff', href: '/staff', show: profile?.role === 'admin' || profile?.role === 'pastor' },
    { label: 'Admin', href: '/admin', show: profile?.role === 'admin' },
  ]?.filter(i => i?.show) : [
    { label: 'Members', href: '/members', show: true },
    { label: 'Visitor Intake', href: '/visitor-intake', show: true },
  ];

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? 'bg-sanctuary/95 backdrop-blur-sm border-b border-border-strong' : 'bg-sanctuary border-b border-border-subtle'}`}>
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
        <Link href="/home" className="flex items-center gap-3">
          <AppLogo size={32} text="Compassion Check-in" className="text-silver" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-2">
          {navItems?.map(item => (
            <Link key={item?.href} href={item?.href} className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-200 ${pathname === item?.href ? 'border-gold text-gold' : 'border-transparent text-silver-dim hover:text-gold hover:border-gold'}`}>
              {item?.label}
            </Link>
          ))}
        </nav>

        {/* User info + logout */}
        <div className="hidden md:flex items-center gap-3">
          {profile ? (
            <>
              <div className="text-right">
                <span className="font-mono text-[10px] tracking-widest uppercase text-gold block">{ROLE_LABELS?.[profile?.role]}</span>
                <span className="font-mono text-[9px] text-silver-dim opacity-60">{profile?.fullName || profile?.email}</span>
              </div>
              <button onClick={handleLogout} className="font-mono text-[10px] tracking-widest uppercase px-4 py-2 border border-border-strong text-silver-dim hover:border-gold hover:text-gold transition-colors">
                Sign Out
              </button>
            </>
          ) : (
            <Link href="/login" className="font-mono text-[10px] tracking-widest uppercase px-4 py-2 border border-gold text-gold hover:bg-gold/10 transition-colors">
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile menu button */}
        <button className="md:hidden text-silver-dim hover:text-gold transition-colors" onClick={() => setMenuOpen(o => !o)} aria-label="Toggle menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
          </svg>
        </button>
      </div>
      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="md:hidden border-t border-border-strong bg-sanctuary">
          {navItems?.map(item => (
            <Link key={item?.href} href={item?.href} onClick={() => setMenuOpen(false)} className={`block font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 border-b border-border-subtle transition-colors ${pathname === item?.href ? 'text-gold' : 'text-silver-dim hover:text-gold'}`}>
              {item?.label}
            </Link>
          ))}
          {profile ? (
            <div className="px-6 py-4 flex items-center justify-between border-b border-border-subtle">
              <div>
                <span className="font-mono text-[10px] tracking-widest uppercase text-gold block">{ROLE_LABELS?.[profile?.role]}</span>
                <span className="font-mono text-[9px] text-silver-dim opacity-60">{profile?.fullName || profile?.email}</span>
              </div>
              <button onClick={handleLogout} className="font-mono text-[10px] tracking-widest uppercase px-3 py-1.5 border border-border-strong text-silver-dim hover:text-gold transition-colors">Sign Out</button>
            </div>
          ) : (
            <Link href="/login" onClick={() => setMenuOpen(false)} className="block font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 text-gold border-b border-border-subtle">Sign In</Link>
          )}
        </div>
      )}
    </header>
  );
}